import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { manualLogout } from '../store/slices/userSlice';

const UserAvatar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.profile);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Get user initials
  const getInitials = (name?: string, username?: string): string => {
    if (name) {
      return name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    }
    if (username) {
      return username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const handleAvatarClick = () => {
    if (!isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 192; // w-48 = 12rem = 192px
      const menuHeight = 120; // approximate height

      // Position menu below the button, aligned to the right
      let left = rect.right - menuWidth;
      let top = rect.bottom + window.scrollY + 8; // 8px gap, account for scroll

      // Ensure menu doesn't go off screen
      if (left < 16) left = 16; // minimum left margin
      if (left + menuWidth > window.innerWidth - 16) {
        left = window.innerWidth - menuWidth - 16;
      }

      // If menu would go off bottom, position above button
      if (top + menuHeight > window.innerHeight + window.scrollY - 16) {
        top = rect.top + window.scrollY - menuHeight - 8;
      }

      setMenuPosition({ top, left });
    }
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigateToTodos = () => {
    navigate('/todos');
    setIsMenuOpen(false);
  };

  const handleNavigateToHabits = () => {
    navigate('/habits');
    setIsMenuOpen(false);
  };

  const handleNavigateToNotes = () => {
    navigate('/notes');
    setIsMenuOpen(false);
  };

  const handleNavigateToTrackers = () => {
    navigate('/trackers');
    setIsMenuOpen(false);
  };

  const handleNavigateToProfile = () => {
    navigate('/profile');
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    dispatch(manualLogout());
    setIsMenuOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!user) {
    return null;
  }

  const initials = getInitials(user.name, user.username);

  return (
    <>
      <div className="relative overflow-visible" ref={menuRef}>
        <button
          ref={buttonRef}
          onClick={handleAvatarClick}
          className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-semibold text-sm hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-0"
          aria-label="User menu"
        >
          {initials}
        </button>
      </div>

      {isMenuOpen && createPortal(
        <div
          ref={menuRef}
          className="absolute w-48 bg-white dark:bg-gray-800 rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-[9999] border border-gray-200 dark:border-gray-700"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          <div className="py-1">
            <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
              <div className="font-medium">{user.name || user.username}</div>
              <div className="text-gray-500 dark:text-gray-400">{user.email}</div>
            </div>
            <button
              onClick={handleNavigateToProfile}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {t('userAvatar.profile')}
            </button>
            <button
              onClick={handleNavigateToTodos}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {t('userAvatar.todos')}
            </button>
            <button
              onClick={handleNavigateToHabits}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {t('userAvatar.habits')}
            </button>
            <button
              onClick={handleNavigateToNotes}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {t('userAvatar.notes')}
            </button>
            <button
              onClick={handleNavigateToTrackers}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {t('userAvatar.trackers')}
            </button>
            <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {t('userAvatar.logout')}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default UserAvatar;
